import {
    IMappingProfile,
    IQueryParams,
    IQueryParamsSymbol,
    PFItemCatalogEntities,
    SafeAny,
} from '@pf/shared-common';
import { createMap, forMember, mapFrom, Mapper } from '@automapper/core';
import {
    entitySymbols,
    withDefaultEntityMetadata,
    withDefaultSearchParamsMappingConfiguration,
    withDefaultSearchParamsMetadata,
    withMetadata,
} from '@pf/shared-utility';
import {
    CategoryViewModelDto,
    ItemCreate,
    ItemDto,
    ItemRead,
    ItemSearchParams,
} from '../../entities/item.dto';
import {
    CustomFieldMetadataMap,
    DimensionsMetadataMap,
    ResourceAuthSymbol,
    WeightMetadataMap,
} from '@pf/shared/util-platform';
import {
    ItemBrandViewModel,
    ItemManufacturerViewModel,
} from '@control-tower/platform-item-catalog';

const symbols = entitySymbols(PFItemCatalogEntities.Item);
const itemBrandSymbol = 'ItemBrandViewModel';
const categoryViewModelSymbol = 'CategoryViewModel';
const itemManufacturerSymbol = 'ItemManufacturerViewModel';
const itemWarehouseAmountRowSymbol = 'ItemWarehouseAmountRow';

withMetadata<ItemBrandViewModel>(itemBrandSymbol, {
    id: String,
    name: String,
    code: String,
    description: String,
    manufacturer: itemManufacturerSymbol,
    customFields: [CustomFieldMetadataMap.symbols.entity],
});
withDefaultEntityMetadata<CategoryViewModelDto>(categoryViewModelSymbol, {
    name: String,
    code: String,
    parentCategoryId: String,
    topLevelCategoryID: String,
    categoryBreadCrumbs: String,
    isTopLevelCategory: Boolean,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    categoryType: Object,
});
withMetadata<ItemManufacturerViewModel>(itemManufacturerSymbol, {
    id: String,
    name: String,
    code: String,
    description: String,
    customFields: [CustomFieldMetadataMap.symbols.entity],
});

withMetadata(itemWarehouseAmountRowSymbol, {
    warehouseId: String,
    primaryAmount: Number,
    secondaryAmount: Number,
    totalWarehouseAmount: Number,
});

withDefaultEntityMetadata<ItemDto>(symbols.dto, {
    name: String,
    code: String,
    description: String,
    brandId: String,
    brand: itemBrandSymbol,
    parentItemId: String,
    parentItemName: String,
    manufacturerName: String,
    sku: String,
    modelNumber: String,
    partNumber: String,
    isBreakPack: Boolean,
    hasInstances: Boolean,
    packSize: Number,
    packSizeUom: String,
    maxGroupSize: Number,
    assignedCategories: [categoryViewModelSymbol],
    hazardous: Boolean,
    weight: WeightMetadataMap.symbols.dto,
    dimensions: DimensionsMetadataMap.symbols.dto,
    vendorSku: String,
    replenishmentStrategy: String,
    defaultMinQuantity: Number,
    defaultMaxQuantity: Number,
    velocity: String,
    resourceAuth: ResourceAuthSymbol,
    customFields: [CustomFieldMetadataMap.symbols.dto],
    warehouseAmounts: [itemWarehouseAmountRowSymbol],
    files: [Object],
});

withDefaultEntityMetadata<ItemRead>(symbols.entity, {
    name: String,
    code: String,
    description: String,
    brandId: String,
    brand: itemBrandSymbol,
    parentItemId: String,
    sku: String,
    modelNumber: String,
    partNumber: String,
    isBreakPack: Boolean,
    hasInstances: Boolean,
    packSize: Number,
    packSizeUom: String,
    maxGroupSize: Number,
    resourceAuth: ResourceAuthSymbol,
    customFields: [CustomFieldMetadataMap.symbols.entity],
    assignedCategories: [categoryViewModelSymbol],
    hazardous: Boolean,
    weight: WeightMetadataMap.symbols.entity,
    dimensions: DimensionsMetadataMap.symbols.entity,
    vendor: Object,
    externalReferenceId: String,
    vendorSku: String,
    replenishmentStrategy: String,
    defaultMinQuantity: Number,
    defaultMaxQuantity: Number,
    velocity: String,
    warehouseAmounts: [itemWarehouseAmountRowSymbol],
    files: [Object],
});

withMetadata<ItemCreate>(symbols.createBody, {
    name: String,
    code: String,
    description: String,
    brandId: String,
    parentItemId: String,
    sku: String,
    modelNumber: String,
    partNumber: String,
    isBreakPack: Boolean,
    hasInstances: Boolean,
    packSize: Number,
    packSizeUom: String,
    maxGroupSize: Number,
    resourceAuth: ResourceAuthSymbol,
    hazardous: Boolean,
    weight: WeightMetadataMap.symbols.createBody,
    dimensions: DimensionsMetadataMap.symbols.createBody,
    externalReferenceId: String,
    vendorId: String,
    vendorSku: String,
    replenishmentStrategy: String,
    defaultMinQuantity: Number,
    defaultMaxQuantity: Number,
    velocity: String,
    warehouseAmounts: [itemWarehouseAmountRowSymbol],
});

withDefaultSearchParamsMetadata<ItemSearchParams>(symbols.searchParams, {
    code: String,
    search: String,
    manufacturerIds: [String],
    customFieldKeys: [String],
    customFieldValue: String,
    hazardous: Boolean,
    isBreakPack: Boolean,
    include: String,
});

export const ItemMetadataMap: IMappingProfile = {
    symbols,
    profile: (mapper: Mapper) => {
        createMap<ItemRead, ItemDto>(
            mapper,
            symbols.entity,
            symbols.dto,
            forMember(
                d => d.files,
                mapFrom(s => {
                    const x = s as SafeAny;
                    return x.files ?? x.Files ?? null;
                })
            ),
            forMember(
                d => d.packSize,
                mapFrom(s => s.packSize)
            ),
            forMember(
                d => d.packSizeUom,
                mapFrom(s => s.packSizeUom)
            )
        );
        createMap<ItemDto, ItemRead>(
            mapper,
            symbols.dto,
            symbols.entity,
            forMember(
                d => d.files,
                mapFrom(s => s.files ?? null)
            ),
            forMember(
                d => d.packSize,
                mapFrom(s => s.packSize)
            ),
            forMember(
                d => d.packSizeUom,
                mapFrom(s => s.packSizeUom)
            )
        );
        createMap<ItemDto, ItemCreate>(
            mapper,
            symbols.dto,
            symbols.createBody,
            forMember(
                d => d.packSize,
                mapFrom(s => s.packSize)
            ),
            forMember(
                d => d.packSizeUom,
                mapFrom(s => s.packSizeUom)
            )
        );
        createMap<IQueryParams<ItemDto>, ItemSearchParams>(
            mapper,
            IQueryParamsSymbol,
            symbols.searchParams,
            forMember(
                d => d.search,
                mapFrom(s => {
                    return s.filters?.name ? s.filters.name : null;
                })
            ),
            forMember(
                d => d.code,
                mapFrom(s => {
                    return s.filters?.sku || s.filters?.code;
                })
            ),
            forMember(
                d => d.categoryIds,
                mapFrom(s => {
                    if (!s.filters?.assignedCategories) {
                        return null;
                    }
                    return typeof s.filters?.assignedCategories === 'string'
                        ? [s.filters?.assignedCategories]
                        : s.filters?.assignedCategories;
                })
            ),
            forMember(
                d => d.manufacturerIds,
                mapFrom(s => {
                    const mid = (s.filters as SafeAny)?.manufacturerId;
                    if (!mid) {
                        return null;
                    }
                    return Array.isArray(mid) ? mid : [mid];
                })
            ),
            forMember(
                d => d.customFieldKeys,
                mapFrom(s => {
                    const k = (s.filters as SafeAny)?.customFieldKey;
                    return k ? [k] : null;
                })
            ),
            forMember(
                d => d.customFieldValue,
                mapFrom(s => {
                    const v = (s.filters as SafeAny)?.customFieldValue;
                    return v ? String(v) : null;
                })
            ),
            forMember(
                d => d.hazardous,
                mapFrom(s =>
                    (s.filters as SafeAny)?.hazardous === true
                        ? true
                        : undefined
                )
            ),
            forMember(
                d => d.isBreakPack,
                mapFrom(s =>
                    (s.filters as SafeAny)?.isBreakPack === true
                        ? true
                        : undefined
                )
            ),
            ...withDefaultSearchParamsMappingConfiguration(),
            forMember(
                d => d.include,
                mapFrom(s => {
                    const v = (s as SafeAny).includeFiles;
                    return v === false ? '' : undefined;
                })
            )
        );

        CustomFieldMetadataMap.profile(mapper);
        WeightMetadataMap.profile(mapper);
        DimensionsMetadataMap.profile(mapper);
    },
};
